import { useState, useEffect, useCallback, useRef } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { cityAPI, uploadAPI, BASE_URL } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0', gap: '12px', flexWrap: 'wrap' },
  searchInput: { flex: 1, maxWidth: '320px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  filterSelect: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', color: '#475569' },
  totalChip: { fontSize: '13px', color: '#64748b', padding: '6px 12px', backgroundColor: '#f1f5f9', borderRadius: '8px', fontWeight: '500' },

  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '720px' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  thRight: { textAlign: 'right' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  tdRight: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', verticalAlign: 'middle' },
  cityName: { fontWeight: '600', color: '#1e293b' },
  cityValue: { fontSize: '12px', color: '#94a3b8', marginTop: '2px' },
  // Circular preview matches how the image actually renders in the public
  // CityModal carousel — admins see the real cropped result, not a misleading
  // landscape rectangle.
  thumb: { width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', backgroundColor: '#f1f5f9', display: 'block', border: '2px solid #e2e8f0' },
  thumbPlaceholder: { width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#f8fafc', border: '2px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '10px' },

  toggle: { position: 'relative', width: '40px', height: '22px', backgroundColor: '#e2e8f0', borderRadius: '11px', cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0 },
  toggleOn: { backgroundColor: '#4f46e5' },
  toggleKnob: { position: 'absolute', top: '2px', left: '2px', width: '18px', height: '18px', backgroundColor: '#fff', borderRadius: '50%', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
  toggleKnobOn: { left: '20px' },

  manageBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', color: '#475569', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  manageBtnPrimary: { borderColor: '#c7d2fe', backgroundColor: '#eef2ff', color: '#4f46e5' },

  emptyState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  loadingState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', margin: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  // Image modal
  modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 },
  closeBtn: { width: '32px', height: '32px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBody: { padding: '24px' },
  // Square 1:1 preview — matches the circular crop the public site applies.
  // Admins see exactly how the image will be cropped before saving.
  imagePreview: { width: '220px', height: '220px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #eef2ff', display: 'block', margin: '0 auto 16px', backgroundColor: '#f8fafc', boxShadow: '0 4px 16px rgba(79,70,229,0.08)' },
  imagePlaceholder: { width: '220px', height: '220px', borderRadius: '50%', border: '2px dashed #cbd5e1', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px', margin: '0 auto 16px' },
  modalActions: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  secondaryButton: { padding: '10px 20px', backgroundColor: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  dangerButton: { padding: '10px 20px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  hint: { fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '12px' },
  buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
};

const toAbsolute = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const filenameFromUrl = (url) => {
  if (!url) return '';
  const m = String(url).match(/city-[^/?#]+/);
  return m ? m[0] : '';
};

const Cities = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [popularFilter, setPopularFilter] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [busyIds, setBusyIds] = useState(new Set());

  const [imageCity, setImageCity] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [modalError, setModalError] = useState('');
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 25 };
      if (search) params.search = search;
      if (popularFilter) params.popular = popularFilter;
      const response = await cityAPI.getAll(params);
      const payload = response.data || {};
      // getAllCities returns { data, pagination } when page is set (which we always do)
      setItems(Array.isArray(payload.data) ? payload.data : []);
      setTotalPages(payload.pagination?.totalPages || 1);
      setTotal(payload.pagination?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  }, [page, search, popularFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setPage]);

  const markBusy = (id, busy) => {
    setBusyIds(prev => {
      const next = new Set(prev);
      if (busy) next.add(id); else next.delete(id);
      return next;
    });
  };

  // Optimistic toggle — flip in UI, then send PUT. Revert on failure.
  const togglePopular = async (city) => {
    if (busyIds.has(city.id)) return;
    const nextPopular = !city.isPopular;
    markBusy(city.id, true);
    setItems(prev => prev.map(c => c.id === city.id ? { ...c, isPopular: nextPopular, ...(nextPopular ? {} : { imageUrl: null }) } : c));
    try {
      await cityAPI.update(city.id, { isPopular: nextPopular });
    } catch (err) {
      // revert
      setItems(prev => prev.map(c => c.id === city.id ? { ...c, isPopular: city.isPopular, imageUrl: city.imageUrl } : c));
      setError(err?.response?.data?.error || 'Failed to update city');
    } finally {
      markBusy(city.id, false);
    }
  };

  const openImageModal = (city) => {
    setImageCity(city);
    setModalError('');
  };
  const closeImageModal = () => {
    setImageCity(null);
    setModalError('');
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !imageCity) return;
    if (file.size > 5 * 1024 * 1024) {
      setModalError('Image must be 5 MB or smaller.');
      return;
    }
    setUploading(true);
    setModalError('');
    try {
      const upload = await uploadAPI.uploadCityImage(file);
      const url = upload?.data?.relativeUrl || upload?.data?.url;
      if (!url) throw new Error('Upload returned no URL');

      // If replacing an existing image, delete the old asset best-effort
      const oldFilename = filenameFromUrl(imageCity.imageUrl);
      if (oldFilename) {
        try { await uploadAPI.deleteCityImage(oldFilename); } catch { /* ignore */ }
      }

      await cityAPI.update(imageCity.id, { imageUrl: url });
      setItems(prev => prev.map(c => c.id === imageCity.id ? { ...c, imageUrl: url } : c));
      setImageCity(prev => prev ? { ...prev, imageUrl: url } : prev);
    } catch (err) {
      setModalError(err?.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!imageCity) return;
    setUploading(true);
    setModalError('');
    try {
      const oldFilename = filenameFromUrl(imageCity.imageUrl);
      if (oldFilename) {
        try { await uploadAPI.deleteCityImage(oldFilename); } catch { /* ignore */ }
      }
      await cityAPI.update(imageCity.id, { imageUrl: '' });
      setItems(prev => prev.map(c => c.id === imageCity.id ? { ...c, imageUrl: null } : c));
      setImageCity(prev => prev ? { ...prev, imageUrl: null } : prev);
    } catch (err) {
      setModalError(err?.response?.data?.error || 'Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  // Close modal on Escape
  useEffect(() => {
    if (!imageCity) return;
    const onKey = (e) => { if (e.key === 'Escape') closeImageModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [imageCity]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Cities</h1>
        <p style={styles.subtitle}>Mark cities as popular and upload an image for each popular city.</p>
      </div>

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            type="text"
            placeholder="Search by city name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={styles.searchInput}
          />
          <select
            value={popularFilter}
            onChange={(e) => { setPopularFilter(e.target.value); setPage(1); }}
            style={styles.filterSelect}
          >
            <option value="">All Cities</option>
            <option value="true">Popular Only</option>
            <option value="false">Not Popular</option>
          </select>
          <span style={styles.totalChip}>{total} {total === 1 ? 'city' : 'cities'}</span>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.loadingState}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '15px', margin: 0 }}>No cities found.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Image</th>
                  <th style={styles.th}>City</th>
                  <th style={styles.th}>Popular</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const busy = busyIds.has(c.id);
                  return (
                    <tr key={c.id}>
                      <td style={styles.td}>
                        {c.isPopular && c.imageUrl ? (
                          <img src={toAbsolute(c.imageUrl)} alt="" style={styles.thumb} />
                        ) : (
                          <div style={styles.thumbPlaceholder}>{c.isPopular ? 'No image' : '—'}</div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.cityName}>{c.name}</div>
                        <div style={styles.cityValue}>{c.value}</div>
                      </td>
                      <td style={styles.td}>
                        <div
                          role="button"
                          onClick={() => togglePopular(c)}
                          style={{
                            ...styles.toggle,
                            ...(c.isPopular ? styles.toggleOn : {}),
                            ...(busy ? styles.buttonDisabled : {}),
                          }}
                          title={c.isPopular ? 'Click to unmark as popular' : 'Click to mark as popular'}
                        >
                          <div style={{ ...styles.toggleKnob, ...(c.isPopular ? styles.toggleKnobOn : {}) }} />
                        </div>
                      </td>
                      <td style={styles.tdRight}>
                        {c.isPopular ? (
                          <button
                            style={{ ...styles.manageBtn, ...styles.manageBtnPrimary }}
                            onClick={() => openImageModal(c)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></svg>
                            {c.imageUrl ? 'Change Image' : 'Add Image'}
                          </button>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>Mark as popular to add image</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && items.length > 0 && totalPages > 1 && (
          <div style={styles.pagination}>
            <span style={styles.pageInfo}>Page {page} of {totalPages}</span>
            <div style={styles.pageButtons}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }}
              >Prev</button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {imageCity && (
        <div style={styles.modal} onClick={closeImageModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{imageCity.name} — Image</h2>
              <button type="button" onClick={closeImageModal} style={styles.closeBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={styles.modalBody}>
              {imageCity.imageUrl ? (
                <img src={toAbsolute(imageCity.imageUrl)} alt={imageCity.name} style={styles.imagePreview} />
              ) : (
                <div style={styles.imagePlaceholder}>No image uploaded yet</div>
              )}

              {modalError && (
                <div style={{ ...styles.errorBox, margin: '0 0 12px 0' }}>{modalError}</div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImagePick}
                style={{ display: 'none' }}
              />

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{ ...styles.primaryButton, ...(uploading ? styles.buttonDisabled : {}) }}
                >
                  {uploading ? 'Uploading…' : imageCity.imageUrl ? 'Replace Image' : 'Upload Image'}
                </button>
                {imageCity.imageUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    disabled={uploading}
                    style={{ ...styles.dangerButton, ...(uploading ? styles.buttonDisabled : {}) }}
                  >
                    Remove
                  </button>
                )}
                <button type="button" onClick={closeImageModal} style={styles.secondaryButton}>Close</button>
              </div>

              <p style={styles.hint}>PNG, JPG, WebP. Max 5 MB. Best results: 4:3 aspect ratio, ≥600×450.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cities;
